import { computed, onMounted, ref, watch } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';

export function useMazdaRedisQuery(mazdaPost, serverName) {
  const redisQueryMode = ref('nonVersion');
  const redisVersionOptions = ref([]);
  const redisNonVersionOptions = ref([]);
  const redisVersionChildOptions = ref([]);
  const redisSelectedVersion = ref('');
  const redisSelectedVersionChild = ref('');
  const redisSelectedNonVersion = ref('');
  const redisQueryItems = ref([]);
  const redisQueryLoading = ref(false);
  const redisQueryExecuted = ref(false);
  const redisKeyword = ref('');
  const redisPage = ref(1);
  const redisPageSize = ref(10);
  const redisJumpPage = ref('1');

  const seachcarIverMigrationLoading = ref(false);
  const seachcarIverMigrationStatusLoaded = ref(false);
  const seachcarIverMigrationCompleted = ref(false);
  const seachcarIverMigrationTargetVersion = ref('V44.40');
  const seachcarIverOfficialVersion = ref('');

  const addRedisDialogVisible = ref(false);
  const redisAddLeafKey = ref('');
  const redisAddValueType = ref('string');
  const redisAddContent = ref('');
  const redisAddSubmitting = ref(false);

  const show_key_value = ref(false);
  const redisDetailFullKey = ref('');
  const redisDetailType = ref('');
  const redisDetailRawValue = ref('');
  const redisDetailJsonAvailable = ref(false);
  const redisDetailJsonObject = ref(null);
  const redisDetailViewMode = ref('raw');
  const redisDetailNumberBase = ref('dec');
  const redisDetailRawEditValue = ref('');
  const redisDetailJsonEditValue = ref('');
  const redisDetailSaving = ref(false);
  const redisDetailRawDirty = ref(false);
  const redisDetailJsonDirty = ref(false);

  const redisCurrentPrefix = computed(() => {
    if (redisQueryMode.value === 'nonVersion') {
      return redisSelectedNonVersion.value ? `api:olinfo:${redisSelectedNonVersion.value}:` : '';
    }
    if (redisSelectedVersion.value && redisSelectedVersionChild.value) {
      return `api:olinfo:${redisSelectedVersion.value}:${redisSelectedVersionChild.value}:`;
    }
    return '';
  });

  const showSeachcarIverMigration = computed(() => (
    redisQueryMode.value === 'nonVersion'
    && redisSelectedNonVersion.value === 'seachcar'
    && seachcarIverMigrationStatusLoaded.value
    && !seachcarIverMigrationCompleted.value
  ));

  const guessRedisValueTypeByPrefix = () => {
    const prefix = String(redisCurrentPrefix.value || '').toLowerCase();
    // 现有 Mazda 数据里 api:olinfo:seachcar:* 通常是 Hash(carid/result/iver)，新增时默认按 Hash 处理。
    if (prefix.includes(':seachcar:') || prefix.includes(':searchcar:')) {
      return 'hash';
    }
    return 'string';
  };

  const redisSuggestedValueType = computed(() => {
    const items = Array.isArray(redisQueryItems.value) ? redisQueryItems.value : [];
    if (!items.length) return guessRedisValueTypeByPrefix();
    const firstType = items[0]?.valueType || 'string';
    return items.every((item) => (item?.valueType || 'string') === firstType) ? firstType : guessRedisValueTypeByPrefix();
  });

  const redisSuggestedValueTypeLabel = computed(() => redisSuggestedValueType.value || 'string');

  const redisAddFullKey = computed(() => {
    const prefix = redisCurrentPrefix.value;
    const leafKey = String(redisAddLeafKey.value || '').trim();
    if (!prefix || !leafKey) return '';
    return `${prefix}${leafKey}`;
  });

  const buildDefaultRedisAddContent = (type) => {
    if (type !== 'hash') return '';
    const prefix = String(redisCurrentPrefix.value || '').toLowerCase();
    if (prefix.includes(':seachcar:') || prefix.includes(':searchcar:')) {
      return JSON.stringify({
        carid: '',
        result: '',
        iver: seachcarIverOfficialVersion.value || seachcarIverMigrationTargetVersion.value,
      }, null, 2);
    }
    return JSON.stringify({ field: '' }, null, 2);
  };

  const redisFilteredItems = computed(() => {
    const list = Array.isArray(redisQueryItems.value) ? redisQueryItems.value : [];
    const keyword = String(redisKeyword.value || '').trim().toLowerCase();
    if (!keyword) return list;
    return list.filter((item) => [item?.leafKey, item?.fullKey, item?.valueType, item?.valuePreview]
      .some((field) => String(field ?? '').toLowerCase().includes(keyword)));
  });

  const redisTotalPages = computed(() => {
    const total = redisFilteredItems.value.length;
    const size = Math.max(1, Number(redisPageSize.value || 10));
    return Math.max(1, Math.ceil(total / size));
  });

  const redisPagedItems = computed(() => {
    const page = Math.min(Math.max(1, Number(redisPage.value || 1)), redisTotalPages.value);
    const size = Math.max(1, Number(redisPageSize.value || 10));
    const start = (page - 1) * size;
    return redisFilteredItems.value.slice(start, start + size);
  });

  const formatNumberToHex = (num) => {
    const abs = Math.trunc(num);
    if (abs < 0) {
      return '-0x' + Math.abs(abs).toString(16).toUpperCase();
    }
    return '0x' + abs.toString(16).toUpperCase();
  };

  const convertJsonNumbers = (value, mode) => {
    if (Array.isArray(value)) {
      return value.map((item) => convertJsonNumbers(item, mode));
    }
    if (value && typeof value === 'object') {
      const result = {};
      Object.keys(value).forEach((key) => {
        result[key] = convertJsonNumbers(value[key], mode);
      });
      return result;
    }
    if (typeof value === 'number' && Number.isInteger(value)) {
      return mode === 'hex' ? formatNumberToHex(value) : value;
    }
    return value;
  };

  const tryParseRedisDetailJson = (value) => {
    if (typeof value !== 'string') {
      return value;
    }
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    if (!(trimmed.startsWith('{') || trimmed.startsWith('['))) {
      return null;
    }
    try {
      return JSON.parse(trimmed);
    } catch {
      return null;
    }
  };

  const buildRedisJsonEditorText = () => {
    if (!redisDetailJsonAvailable.value || redisDetailJsonObject.value === null) {
      return '';
    }
    try {
      const converted = convertJsonNumbers(redisDetailJsonObject.value, redisDetailNumberBase.value);
      return JSON.stringify(converted, null, 2);
    } catch {
      return redisDetailRawValue.value || '';
    }
  };

  const redisDetailEditorText = computed({
    get() {
      if (redisDetailViewMode.value === 'json' && redisDetailJsonAvailable.value) {
        return redisDetailJsonEditValue.value;
      }
      return redisDetailRawEditValue.value;
    },
    set(value) {
      if (redisDetailViewMode.value === 'json' && redisDetailJsonAvailable.value) {
        redisDetailJsonEditValue.value = value;
        redisDetailJsonDirty.value = true;
        return;
      }
      redisDetailRawEditValue.value = value;
      redisDetailRawDirty.value = true;
    },
  });

  const buildRedisValuePreview = (value) => {
    if (typeof value === 'string') {
      return value.length > 200 ? `${value.slice(0, 200)}...` : value;
    }
    try {
      const text = JSON.stringify(value ?? {}, null, 2);
      return text.length > 200 ? `${text.slice(0, 200)}...` : text;
    } catch {
      const text = String(value ?? '');
      return text.length > 200 ? `${text.slice(0, 200)}...` : text;
    }
  };

  const getLeafKeyFromFullKey = (fullKey) => {
    if (!fullKey) return '';
    const parts = String(fullKey).split(':');
    return parts[parts.length - 1] || '';
  };

  const parseIntegerByText = (value) => {
    if (typeof value === 'number') {
      return Math.trunc(value);
    }
    if (typeof value !== 'string') {
      return value;
    }
    const trimmed = value.trim();
    if (/^-?0x[0-9a-f]+$/i.test(trimmed)) {
      const negative = trimmed.startsWith('-');
      const body = negative ? trimmed.slice(3) : trimmed.slice(2);
      const parsed = parseInt(body, 16);
      return Number.isNaN(parsed) ? value : (negative ? -parsed : parsed);
    }
    if (/^-?\d+$/.test(trimmed)) {
      const parsed = Number(trimmed);
      return Number.isNaN(parsed) ? value : parsed;
    }
    return value;
  };

  const normalizeEditedJsonByTemplate = (edited, template) => {
    if (Array.isArray(edited)) {
      const templateArray = Array.isArray(template) ? template : [];
      return edited.map((item, index) => normalizeEditedJsonByTemplate(item, templateArray[index]));
    }

    if (edited && typeof edited === 'object') {
      const templateObject = template && typeof template === 'object' && !Array.isArray(template) ? template : {};
      const result = {};
      Object.keys(edited).forEach((key) => {
        result[key] = normalizeEditedJsonByTemplate(edited[key], templateObject[key]);
      });
      return result;
    }

    if (typeof template === 'number' && Number.isInteger(template)) {
      return parseIntegerByText(edited);
    }

    return edited;
  };

  const buildRedisSavePayload = () => {
    const payload = {
      fullKey: redisDetailFullKey.value,
      valueType: redisDetailType.value || 'string',
    };

    if (redisDetailViewMode.value === 'json' && redisDetailJsonAvailable.value) {
      let parsed;
      try {
        parsed = JSON.parse(redisDetailJsonEditValue.value || '');
      } catch {
        throw new Error('JSON 解析内容不是合法 JSON');
      }

      const normalized = normalizeEditedJsonByTemplate(parsed, redisDetailJsonObject.value);
      payload.content = redisDetailJsonEditValue.value;
      payload.jsonValue = normalized;
      return payload;
    }

    payload.content = redisDetailRawEditValue.value ?? '';

    if (payload.valueType === 'hash') {
      let parsed;
      try {
        parsed = JSON.parse(payload.content || '');
      } catch {
        throw new Error('Hash 类型原始内容必须是合法 JSON 对象');
      }
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Hash 类型原始内容必须是 JSON 对象');
      }
      payload.jsonValue = parsed;
    }

    return payload;
  };

  const applyRedisDetailRow = (row) => {
    redisDetailFullKey.value = row?.fullKey || '';
    redisDetailType.value = row?.valueType || '';
    redisDetailViewMode.value = 'raw';
    redisDetailNumberBase.value = 'dec';
    redisDetailRawDirty.value = false;
    redisDetailJsonDirty.value = false;

    const value = row?.value;
    let rawText = '';
    if (typeof value === 'string') {
      rawText = value;
    } else {
      try {
        rawText = JSON.stringify(value ?? {}, null, 2);
      } catch {
        rawText = String(value ?? '');
      }
    }

    redisDetailRawValue.value = rawText;
    redisDetailRawEditValue.value = rawText;

    const parsed = tryParseRedisDetailJson(value);
    if (parsed !== null) {
      redisDetailJsonAvailable.value = true;
      redisDetailJsonObject.value = parsed;
      redisDetailJsonEditValue.value = buildRedisJsonEditorText();
      redisDetailViewMode.value = 'json';
    } else {
      redisDetailJsonAvailable.value = false;
      redisDetailJsonObject.value = null;
      redisDetailJsonEditValue.value = '';
    }
  };

  const resetRedisPagination = () => {
    redisPage.value = 1;
    redisJumpPage.value = '1';
  };

  const sortRedisItemsByKey = (items) => [...items].sort((a, b) => String(a?.fullKey || '').localeCompare(String(b?.fullKey || '')));

  const loadRedisVersionChildren = async () => {
    if (!redisSelectedVersion.value) {
      redisVersionChildOptions.value = [];
      redisSelectedVersionChild.value = '';
      return;
    }

    redisQueryLoading.value = true;
    try {
      const response = await mazdaPost('queryRedisChildren', {
        version: redisSelectedVersion.value,
      });
      const data = response.data || {};
      if (data.success) {
        redisVersionChildOptions.value = Array.isArray(data.children) ? data.children : [];
        if (!redisVersionChildOptions.value.includes(redisSelectedVersionChild.value)) {
          redisSelectedVersionChild.value = redisVersionChildOptions.value[0] || '';
        }
      } else {
        ElMessage.error(data.message || '读取版本下级分类失败');
      }
    } catch (error) {
      ElMessage.error(`读取版本下级分类失败: ${error.response?.data?.message || error.message}`);
    } finally {
      redisQueryLoading.value = false;
    }
  };

  const loadSeachcarIverMigrationStatus = async ({ silent = false } = {}) => {
    if (redisSelectedNonVersion.value !== 'seachcar') {
      seachcarIverMigrationStatusLoaded.value = false;
      seachcarIverMigrationCompleted.value = false;
      return;
    }

    try {
      const response = await mazdaPost('querySeachcarIverMigrationStatus');
      const data = response.data || {};
      if (!data.success) {
        seachcarIverMigrationStatusLoaded.value = false;
        if (!silent) ElMessage.error(data.message || '读取 seachcar iVer 迁移状态失败');
        return;
      }

      seachcarIverMigrationTargetVersion.value = data.targetVersion || 'V44.40';
      seachcarIverOfficialVersion.value = data.officialVersion || '';
      seachcarIverMigrationCompleted.value = Boolean(data.completed);
      seachcarIverMigrationStatusLoaded.value = true;
    } catch (error) {
      seachcarIverMigrationStatusLoaded.value = false;
      if (!silent) {
        ElMessage.error(`读取 seachcar iVer 迁移状态失败: ${error.response?.data?.message || error.message}`);
      }
    }
  };

  const loadRedisTopLevel = async () => {
    redisQueryLoading.value = true;
    try {
      const response = await mazdaPost('queryRedisTopLevel');
      const data = response.data || {};
      if (data.success) {
        redisVersionOptions.value = Array.isArray(data.versions) ? data.versions : [];
        redisNonVersionOptions.value = Array.isArray(data.nonVersions) ? data.nonVersions : [];
        if (!redisSelectedNonVersion.value && redisNonVersionOptions.value.length) {
          redisSelectedNonVersion.value = redisNonVersionOptions.value[0];
        }
        if (!redisSelectedVersion.value && redisVersionOptions.value.length) {
          redisSelectedVersion.value = redisVersionOptions.value[0];
        }
        if (redisSelectedVersion.value) {
          await loadRedisVersionChildren();
        }
        if (redisSelectedNonVersion.value === 'seachcar') {
          await loadSeachcarIverMigrationStatus({ silent: true });
        }
      } else {
        ElMessage.error(data.message || '读取 Redis 顶级分类失败');
      }
    } catch (error) {
      ElMessage.error(`读取 Redis 顶级分类失败: ${error.response?.data?.message || error.message}`);
    } finally {
      redisQueryLoading.value = false;
    }
  };

  const runSeachcarIverMigration = async () => {
    try {
      await ElMessageBox.confirm(
        `该操作会给所有 api:olinfo:seachcar:* Hash 补充 iver=${seachcarIverMigrationTargetVersion.value}。\n\n全部执行成功后，此按钮将永久隐藏。确定继续吗？`,
        '确认一次性迁移',
        {
          confirmButtonText: '开始执行',
          cancelButtonText: '取消',
          type: 'warning',
        },
      );
    } catch {
      return;
    }

    seachcarIverMigrationLoading.value = true;
    try {
      const response = await mazdaPost('migrateSeachcarIver', {
        targetVersion: seachcarIverMigrationTargetVersion.value,
      });
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || 'seachcar iVer 迁移失败');
        await loadSeachcarIverMigrationStatus({ silent: true });
        return;
      }

      seachcarIverMigrationCompleted.value = true;
      seachcarIverMigrationStatusLoaded.value = true;
      seachcarIverOfficialVersion.value = data.officialVersion || seachcarIverOfficialVersion.value;
      ElMessage.success(data.message || 'seachcar iVer 迁移成功');
      if (redisQueryExecuted.value) {
        await loadRedisItems();
      }
    } catch (error) {
      ElMessage.error(`seachcar iVer 迁移失败: ${error.response?.data?.message || error.message}`);
      await loadSeachcarIverMigrationStatus({ silent: true });
    } finally {
      seachcarIverMigrationLoading.value = false;
    }
  };

  const handleRedisVersionChange = async () => {
    redisSelectedVersionChild.value = '';
    redisQueryItems.value = [];
    redisQueryExecuted.value = false;
    redisKeyword.value = '';
    resetRedisPagination();
    await loadRedisVersionChildren();
  };

  const loadRedisItems = async () => {
    redisQueryLoading.value = true;
    try {
      let payload = {};
      if (redisQueryMode.value === 'nonVersion') {
        if (!redisSelectedNonVersion.value) {
          ElMessage.warning('请先选择非版本分类');
          return;
        }
        payload = {
          mode: 'nonVersion',
          nonVersion: redisSelectedNonVersion.value,
          fetchAll: true,
        };
      } else {
        if (!redisSelectedVersion.value || !redisSelectedVersionChild.value) {
          ElMessage.warning('请先选择版本和下一级分类');
          return;
        }
        payload = {
          mode: 'version',
          version: redisSelectedVersion.value,
          child: redisSelectedVersionChild.value,
          fetchAll: true,
        };
      }

      const response = await mazdaPost('queryRedisItems', payload);
      const data = response.data || {};
      if (data.success) {
        redisQueryItems.value = sortRedisItemsByKey(Array.isArray(data.items) ? data.items : []);
        redisQueryExecuted.value = true;
        resetRedisPagination();
        if (Array.isArray(data.errors) && data.errors.length) {
          ElMessage.warning(`有 ${data.errors.length} 条 Redis 数据读取失败，已跳过失败项`);
        }
      } else {
        redisQueryItems.value = [];
        redisQueryExecuted.value = true;
        resetRedisPagination();
        ElMessage.error(data.message || '读取 Redis 内容失败');
      }
    } catch (error) {
      redisQueryItems.value = [];
      redisQueryExecuted.value = true;
      resetRedisPagination();
      ElMessage.error(`读取 Redis 内容失败: ${error.response?.data?.message || error.message}`);
    } finally {
      redisQueryLoading.value = false;
    }
  };

  const handleRedisKeywordChange = () => {
    resetRedisPagination();
  };

  const handleRedisPageChange = (page) => {
    redisPage.value = Number(page || 1);
    redisJumpPage.value = String(redisPage.value);
  };

  const handleRedisSizeChange = (size) => {
    redisPageSize.value = Number(size || 10);
    resetRedisPagination();
  };

  const handleRedisJump = () => {
    const page = Number(redisJumpPage.value);
    if (!Number.isFinite(page) || page < 1) {
      redisJumpPage.value = String(redisPage.value);
      return;
    }
    redisPage.value = Math.min(redisTotalPages.value, Math.max(1, Math.floor(page)));
    redisJumpPage.value = String(redisPage.value);
  };

  const openAddRedisDialog = () => {
    if (!redisCurrentPrefix.value) {
      ElMessage.warning('请先完成当前 Redis 查询后再添加数据');
      return;
    }
    redisAddLeafKey.value = '';
    redisAddValueType.value = redisSuggestedValueType.value;
    redisAddContent.value = buildDefaultRedisAddContent(redisAddValueType.value);
    addRedisDialogVisible.value = true;
  };

  const buildRedisAddPayload = () => {
    const fullKey = redisAddFullKey.value;
    if (!fullKey) {
      throw new Error('请先输入新增键值');
    }
    const payload = {
      fullKey,
      valueType: redisAddValueType.value || 'string',
    };
    if (payload.valueType === 'hash') {
      let parsed;
      try {
        parsed = JSON.parse(redisAddContent.value || '');
      } catch {
        throw new Error('Hash 类型内容必须是合法 JSON 对象');
      }
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
        throw new Error('Hash 类型内容必须是 JSON 对象');
      }
      payload.content = redisAddContent.value || '{}';
      payload.jsonValue = parsed;
      return payload;
    }
    payload.content = redisAddContent.value ?? '';
    return payload;
  };

  const submitAddRedisData = async () => {
    let payload;
    try {
      payload = buildRedisAddPayload();
    } catch (error) {
      ElMessage.error(error.message || '新增内容校验失败');
      return;
    }

    try {
      await ElMessageBox.confirm(
        `确定新增以下 Redis 数据吗？\n\n${payload.fullKey}`,
        '确认新增',
        {
          confirmButtonText: '确定新增',
          cancelButtonText: '取消',
          type: 'warning',
        },
      );
    } catch {
      return;
    }

    redisAddSubmitting.value = true;
    try {
      const response = await mazdaPost('add_redis_data', payload);
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '新增 Redis 数据失败');
        return;
      }

      addRedisDialogVisible.value = false;
      ElMessage.success(data.message || '新增 Redis 数据成功');
      await loadRedisItems();
    } catch (error) {
      ElMessage.error(`新增 Redis 数据失败: ${error.response?.data?.message || error.message}`);
    } finally {
      redisAddSubmitting.value = false;
    }
  };

  const showRedisDetail = (row) => {
    applyRedisDetailRow(row);
    show_key_value.value = true;
  };

  const updateRedisItemInList = (row) => {
    const index = redisQueryItems.value.findIndex((item) => item.fullKey === row.fullKey);
    if (index >= 0) {
      redisQueryItems.value[index] = {
        ...redisQueryItems.value[index],
        ...row,
        leafKey: row.leafKey || getLeafKeyFromFullKey(row.fullKey),
        valuePreview: row.valuePreview || buildRedisValuePreview(row.value),
      };
    }
  };

  const buildRedisRowFromSaveResponse = (data) => ({
    fullKey: data.fullKey || redisDetailFullKey.value,
    leafKey: data.leafKey || getLeafKeyFromFullKey(data.fullKey || redisDetailFullKey.value),
    valueType: data.valueType || redisDetailType.value || 'string',
    value: Object.prototype.hasOwnProperty.call(data, 'value') ? data.value : redisDetailRawEditValue.value,
    valuePreview: data.valuePreview || buildRedisValuePreview(Object.prototype.hasOwnProperty.call(data, 'value') ? data.value : redisDetailRawEditValue.value),
  });

  const saveRedisDetail = async () => {
    if (!redisDetailFullKey.value) {
      ElMessage.warning('当前没有可保存的 Redis 数据');
      return;
    }

    let payload;
    try {
      payload = buildRedisSavePayload();
    } catch (error) {
      ElMessage.error(error.message || '内容校验失败');
      return;
    }

    try {
      await ElMessageBox.confirm(
        `确定修改以下 Redis 数据吗？\n\n${redisDetailFullKey.value}`,
        '确认修改',
        {
          confirmButtonText: '确定修改',
          cancelButtonText: '取消',
          type: 'warning',
        },
      );
    } catch {
      return;
    }

    redisDetailSaving.value = true;
    try {
      const response = await mazdaPost('updata_redis_data', payload);
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || 'Redis 数据修改失败');
        return;
      }

      const updatedRow = buildRedisRowFromSaveResponse(data);
      updateRedisItemInList(updatedRow);
      applyRedisDetailRow(updatedRow);
      show_key_value.value = true;
      ElMessage.success(data.message || 'Redis 数据修改成功');
    } catch (error) {
      ElMessage.error(`Redis 数据修改失败: ${error.response?.data?.message || error.message}`);
    } finally {
      redisDetailSaving.value = false;
    }
  };

  const cancelSelect = () => {
    show_key_value.value = false;
    redisDetailRawValue.value = '';
    redisDetailRawEditValue.value = '';
    redisDetailJsonEditValue.value = '';
    redisDetailJsonObject.value = null;
    redisDetailJsonAvailable.value = false;
    redisDetailViewMode.value = 'raw';
    redisDetailNumberBase.value = 'dec';
    redisDetailSaving.value = false;
    redisDetailRawDirty.value = false;
    redisDetailJsonDirty.value = false;
  };

  const deleteRedisDetail = async () => {
    if (!redisDetailFullKey.value) {
      ElMessage.warning('当前没有可删除的 Redis 数据');
      return;
    }

    try {
      await ElMessageBox.confirm(
        `确定删除以下 Redis 数据吗？\n\n${redisDetailFullKey.value}`,
        '确认删除',
        {
          confirmButtonText: '确定删除',
          cancelButtonText: '取消',
          type: 'warning',
        },
      );
    } catch {
      return;
    }

    redisDetailSaving.value = true;
    try {
      const response = await mazdaPost('delete_redis_data', { fullKey: redisDetailFullKey.value });
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '删除 Redis 数据失败');
        return;
      }
      redisQueryItems.value = redisQueryItems.value.filter((item) => item.fullKey !== redisDetailFullKey.value);
      cancelSelect();
      ElMessage.success(data.message || '删除 Redis 数据成功');
    } catch (error) {
      ElMessage.error(`删除 Redis 数据失败: ${error.response?.data?.message || error.message}`);
    } finally {
      redisDetailSaving.value = false;
    }
  };

  const isDefaultRedisAddContent = (current) => {
    if (!current || current === '{}' || current === '{\n  "field": ""\n}') return true;
    try {
      const parsed = JSON.parse(current);
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return false;
      const keys = Object.keys(parsed).sort();
      const isSearchCarShape = keys.every((key) => ['carid', 'iver', 'result'].includes(key))
        && keys.includes('carid')
        && keys.includes('result');
      return isSearchCarShape
        && String(parsed.carid ?? '') === ''
        && String(parsed.result ?? '') === '';
    } catch {
      return false;
    }
  };

  watch(redisAddValueType, (type, oldType) => {
    if (!addRedisDialogVisible.value || type === oldType) return;
    const current = String(redisAddContent.value || '').trim();
    if (isDefaultRedisAddContent(current)) {
      redisAddContent.value = buildDefaultRedisAddContent(type);
    }
  });

  watch([redisFilteredItems, redisPageSize], () => {
    const maxPage = redisTotalPages.value;
    if (redisPage.value > maxPage) {
      redisPage.value = maxPage;
    }
    if (redisPage.value < 1) {
      redisPage.value = 1;
    }
    redisJumpPage.value = String(redisPage.value);
  });

  watch(redisQueryMode, async () => {
    redisQueryItems.value = [];
    redisQueryExecuted.value = false;
    redisKeyword.value = '';
    resetRedisPagination();
    if (redisQueryMode.value === 'nonVersion' && redisSelectedNonVersion.value === 'seachcar') {
      await loadSeachcarIverMigrationStatus({ silent: true });
    }
  });

  watch(redisSelectedNonVersion, async (value) => {
    if (redisQueryMode.value === 'nonVersion') {
      redisQueryItems.value = [];
      redisQueryExecuted.value = false;
      redisKeyword.value = '';
      resetRedisPagination();
    }
    if (value === 'seachcar') {
      await loadSeachcarIverMigrationStatus({ silent: true });
    } else {
      seachcarIverMigrationStatusLoaded.value = false;
      seachcarIverMigrationCompleted.value = false;
    }
  });

  watch(redisSelectedVersionChild, () => {
    if (redisQueryMode.value === 'version') {
      redisQueryItems.value = [];
      redisQueryExecuted.value = false;
      redisKeyword.value = '';
      resetRedisPagination();
    }
  });

  watch([redisDetailViewMode, redisDetailNumberBase], () => {
    if (redisDetailViewMode.value === 'json' && redisDetailJsonAvailable.value && !redisDetailJsonDirty.value) {
      redisDetailJsonEditValue.value = buildRedisJsonEditorText();
    }
  });

  onMounted(loadRedisTopLevel);
  watch(serverName, async () => {
    seachcarIverMigrationStatusLoaded.value = false;
    seachcarIverMigrationCompleted.value = false;
    seachcarIverOfficialVersion.value = '';
    await loadRedisTopLevel();
  });

  return {
    redisQueryMode,
    redisVersionOptions,
    redisNonVersionOptions,
    redisVersionChildOptions,
    redisSelectedVersion,
    redisSelectedVersionChild,
    redisSelectedNonVersion,
    redisQueryItems,
    redisQueryLoading,
    redisQueryExecuted,
    redisKeyword,
    redisPage,
    redisPageSize,
    redisJumpPage,
    seachcarIverMigrationLoading,
    seachcarIverMigrationTargetVersion,
    showSeachcarIverMigration,
    addRedisDialogVisible,
    redisAddLeafKey,
    redisAddValueType,
    redisAddContent,
    redisAddSubmitting,
    show_key_value,
    redisDetailFullKey,
    redisDetailType,
    redisDetailJsonAvailable,
    redisDetailViewMode,
    redisDetailNumberBase,
    redisDetailEditorText,
    redisDetailSaving,
    redisCurrentPrefix,
    redisSuggestedValueTypeLabel,
    redisAddFullKey,
    redisFilteredItems,
    redisTotalPages,
    redisPagedItems,
    loadRedisTopLevel,
    handleRedisVersionChange,
    loadRedisItems,
    runSeachcarIverMigration,
    handleRedisKeywordChange,
    handleRedisPageChange,
    handleRedisSizeChange,
    handleRedisJump,
    openAddRedisDialog,
    submitAddRedisData,
    showRedisDetail,
    saveRedisDetail,
    deleteRedisDetail,
    cancelSelect,
  };
}
